/**
 * BR-04 seven-step campaign builder.
 *
 * Autosave writes the open draft. Submit still goes through the same
 * /submit route as the readiness panel - this screen never decides a
 * gate on its own.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Eyebrow, Money } from "@ui/primitives";
import { TextInput, TextArea, FormError } from "@ui/forms";
import { fromMinorString } from "@contracts/money";
import { fontSize, space } from "@design/index";
import { api } from "@app/shared/api";
import { useToast } from "@app/shared/Toasts";
import {
  BRIEF_FIELDS, DRAFT_PLATFORMS, WIZARD_STEPS,
  canSubmitDraft, firstIncompleteStep, formFromVersion,
  issuesFor, issuesOn, liveCampaignTotalMinor, parseDraftPlatforms, selectedPlatforms,
  type DraftFormState, type DraftVersionInput, type WizardStepId,
} from "./draftForm";
import { saveDraftOnce } from "./draftSave";
import { VersionPreview } from "./VersionPreview";
import { campaignCoverUrl } from "@app/shared/coverUrl";
import { CoverPhoto, type CoverRow } from "./CoverPhoto";
import { PlatformSelect } from "./PlatformSelect";

function fieldError(form: DraftFormState, field: string): string | undefined {
  return issuesFor(form).find((i) => i.field === field)?.message;
}

export function DraftWizard({
  campaignId, displayName, recordVersion, version, currency, onSaved, onSubmit, submitBusy,
  onLiveTotals, cover, canDiscardDraft, onDiscardDraft, discardBusy,
}: {
  campaignId: string;
  displayName: string;
  recordVersion: number;
  version: DraftVersionInput & { version: number };
  currency: string;
  onSaved: () => Promise<void>;
  onSubmit: () => void;
  submitBusy: boolean;
  onLiveTotals?: (totalMinor: number | null) => void;
  cover?: CoverRow | null;
  canDiscardDraft?: boolean;
  onDiscardDraft?: () => void;
  discardBusy?: boolean;
}) {
  const toast = useToast();
  const [form, setForm] = useState(() => formFromVersion(displayName, version));
  const [step, setStep] = useState<WizardStepId>(() => {
    try {
      const stored = sessionStorage.getItem(`cm.draft.step.${campaignId}`);
      if (stored && WIZARD_STEPS.some((s) => s.id === stored)) return stored as WizardStepId;
    } catch { /* private mode */ }
    return firstIncompleteStep(formFromVersion(displayName, version));
  });
  const [rv, setRv] = useState(recordVersion);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const baseline = useRef("");
  const [edited, setEdited] = useState(() => version.summary.trim().length > 0);
  const skipAutosave = useRef(true);
  const persistRef = useRef<(kind: "autosave" | "save" | "send") => Promise<boolean>>(
    async () => false,
  );
  const formRef = useRef(form);
  const rvRef = useRef(rv);
  const persistTail = useRef(Promise.resolve());
  const errorRef = useRef<HTMLDivElement>(null);
  formRef.current = form;
  rvRef.current = rv;
  if (!baseline.current) baseline.current = JSON.stringify(form);

  useEffect(() => {
    if (edited) return;
    if (JSON.stringify(form) !== baseline.current) setEdited(true);
  }, [form, edited]);

  // A parent reload can return a stale record_version. Never rewind past a
  // version we already wrote - that is what turns the next Continue into a
  // silent VERSION_CONFLICT.
  useEffect(() => {
    if (recordVersion > rvRef.current) {
      rvRef.current = recordVersion;
      setRv(recordVersion);
    }
  }, [recordVersion]);
  useEffect(() => {
    try { sessionStorage.setItem(`cm.draft.step.${campaignId}`, step); } catch { /* private mode */ }
  }, [campaignId, step]);

  const persist = useCallback(async (kind: "autosave" | "save" | "send") => {
    // Each caller waits its turn, then saves THIS form. Send for review
    // used to inherit a quiet autosave failure and toast "fix the error"
    // with nothing on the page.
    const prev = persistTail.current;
    let release!: () => void;
    persistTail.current = new Promise<void>((r) => { release = r; });
    await prev;
    setSaving(true);
    try {
      const out = await saveDraftOnce({
        form: formRef.current,
        recordVersion: Number(rvRef.current),
        patch: (body) => api.patch(`/campaigns/${campaignId}/draft`, body),
        refreshVersion: async () => {
          const fresh = await api.get<{ campaign: { record_version: number } }>(
            `/campaigns/${campaignId}`,
          );
          if (!fresh.ok) return null;
          const nextRv = Number(fresh.data.campaign.record_version);
          return Number.isInteger(nextRv) && nextRv > 0 ? nextRv : null;
        },
      });
      if (!out.ok) {
        setError(out.message);
        if (kind !== "autosave") toast.error(out.message);
        return false;
      }
      rvRef.current = out.recordVersion;
      setRv(out.recordVersion);
      setSavedAt(Date.now());
      setError(null);
      if (kind === "save") {
        toast.ok("Draft saved.");
        await onSaved();
      }
      return true;
    } finally {
      setSaving(false);
      release();
    }
  }, [campaignId, onSaved, toast]);

  persistRef.current = persist;
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ block: "center" });
  }, [error]);

  useEffect(() => {
    if (skipAutosave.current) {
      skipAutosave.current = false;
      return;
    }
    const t = window.setTimeout(() => { void persistRef.current("autosave"); }, 900);
    return () => window.clearTimeout(t);
  }, [form]);

  const set = (k: keyof DraftFormState, v: string) =>
    setForm((p) => (k === "brief" ? p : { ...p, [k]: v }));
  const setBrief = (k: string, v: string) =>
    setForm((p) => {
      const next = { ...p, brief: { ...p.brief, [k]: v } };
      if (k !== "allowed_providers") return next;
      const ids = parseDraftPlatforms(v);
      const platform_budgets = { ...next.platform_budgets };
      const platform_cpms = { ...next.platform_cpms };
      for (const id of ids) {
        if (!(id in platform_budgets)) platform_budgets[id] = "";
        if (!(id in platform_cpms)) platform_cpms[id] = "";
      }
      return { ...next, platform_budgets, platform_cpms };
    });
  const setPlatform = (kind: "platform_budgets" | "platform_cpms", id: string, v: string) =>
    setForm((p) => ({ ...p, [kind]: { ...p[kind], [id]: v } }));

  const issues = useMemo(() => issuesFor(form), [form]);
  const liveTotal = useMemo(() => liveCampaignTotalMinor(form), [form]);
  useEffect(() => { onLiveTotals?.(liveTotal); }, [liveTotal, onLiveTotals]);
  const ready = canSubmitDraft(form);
  const idx = WIZARD_STEPS.findIndex((s) => s.id === step);
  const current = WIZARD_STEPS[idx] ?? WIZARD_STEPS[0]!;
  const savedLabel = saving ? "Saving…"
    : savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`
    : "Autosaves as you type";

  const go = (id: WizardStepId) => setStep(id);
  const next = () => {
    // Advance first, persist after the click is done. A quiet save that
    // 409s, trips a CHECK, or sees a mid-edit 0.1 used to swallow Continue
    // on Rewards (step 5). Functional setStep survives a stale idx.
    setStep((current) => {
      const i = WIZARD_STEPS.findIndex((s) => s.id === current);
      return WIZARD_STEPS[i + 1]?.id ?? current;
    });
    queueMicrotask(() => {
      void persist("autosave").catch(() => {
        setError("The draft could not save.");
      });
    });
  };
  const back = () => {
    const before = WIZARD_STEPS[idx - 1];
    if (before) setStep(before.id);
  };

  const send = async () => {
    const currentIssues = issuesFor(formRef.current);
    if (currentIssues.length > 0 || !canSubmitDraft(formRef.current)) {
      const first = currentIssues[0];
      if (first) setStep(first.step);
      const msg = first?.message ?? "Finish the marked fields before sending for review.";
      setError(msg);
      toast.error(msg);
      return;
    }
    const ok = await persist("send");
    if (ok) onSubmit();
  };

  return (
    <Card padding={22} style={{ display: "grid", gap: space(4) }}>
      <div className="cm-wizard-head">
        <div>
          <Eyebrow>Draft v{version.version} · Step {idx + 1} of {WIZARD_STEPS.length}</Eyebrow>
          <p style={{ margin: `${space(1)} 0 0`, fontSize: fontSize.micro, color: "var(--faint)" }}>
            Only you can see this until you send it for review.
          </p>
        </div>
        <div className="cm-wizard-head-actions">
          <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>{savedLabel}</span>
          {canDiscardDraft && onDiscardDraft ? (
            <Button type="button" variant="ghost" size="sm" disabled={discardBusy || saving}
              onClick={onDiscardDraft}>
              {discardBusy ? "Cancelling…" : "Cancel edit"}
            </Button>
          ) : null}
          {edited ? (
            <>
              <Button type="button" variant="secondary" size="sm"
                onClick={() => void persist("save")} disabled={saving}>
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button type="button" variant="primary" size="sm"
                onClick={() => void send()} disabled={submitBusy || saving}>
                {submitBusy ? "Sending…" : "Send for review"}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <nav className="cm-wizard-steps" aria-label="Draft steps">
        {WIZARD_STEPS.map((s, i) => {
          const count = issuesOn(form, s.id).length;
          const state = s.id === step ? "active" : count > 0 ? "issue" : i < idx ? "done" : "todo";
          return (
            <button
              key={s.id}
              type="button"
              className="cm-wizard-step"
              data-state={state}
              aria-current={s.id === step ? "step" : undefined}
              onClick={() => go(s.id)}
            >
              <span className="cm-wizard-step-n">{String(i + 1).padStart(2, "0")}</span>
              <span className="cm-wizard-step-label">{s.title}</span>
              {count > 0 ? <span className="cm-wizard-step-count">{count}</span> : null}
            </button>
          );
        })}
      </nav>

      {error ? (
        <div ref={errorRef}>
          <FormError message={error} />
        </div>
      ) : null}

      <section style={{ display: "grid", gap: space(3) }} aria-labelledby="wizard-step-title">
        <div>
          <h2 id="wizard-step-title" style={{ margin: 0, fontSize: fontSize.h3, fontWeight: 700 }}>
            {current.title}
          </h2>
          <p style={{ margin: `${space(1)} 0 0`, fontSize: fontSize.label, color: "var(--faint)",
                      lineHeight: 1.5 }}>{current.hint}</p>
        </div>

        {step === "basics" ? (
          <>
            <TextInput label="Campaign name" required value={form.display_name}
              error={fieldError(form, "display_name")}
              onChange={(e) => set("display_name", e.currentTarget.value)}
              hint="Creators see this in discovery. You can rename it while it is a draft." />
            <TextArea label="Summary" required rows={3} value={form.summary}
              error={fieldError(form, "summary")}
              onChange={(e) => set("summary", e.currentTarget.value)}
              hint="One or two sentences. This is the tile copy." />
            <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
              Clipping only. Creators cut from the licensed source you attach.
            </p>
          </>
        ) : null}

        {step === "brief" ? (
          BRIEF_FIELDS.map((f) => (
            f.kind === "platforms" ? (
              <PlatformSelect key={f.key} required={f.required} hint={f.hint}
                value={form.brief[f.key] ?? ""}
                error={fieldError(form, f.key)}
                onChange={(v) => setBrief(f.key, v)} />
            ) : (
              <TextArea key={f.key} label={f.label} hint={f.hint} rows={f.rows}
                required={f.required}
                value={form.brief[f.key] ?? ""}
                error={fieldError(form, f.key)}
                onChange={(e) => setBrief(f.key, e.currentTarget.value)} />
            )
          ))
        ) : null}

        {step === "assets" ? (
          <>
            <CoverPhoto campaignId={campaignId} existing={cover ?? null}
              onAttached={() => { void onSaved(); }} />
            <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
              Licensed source files land in quarantine and are only readable once
              they scan clean. Replacing a file does not remove the version a
              creator already accepted.
            </p>
            <TextArea label="Source files to attach" rows={4} value={form.source_assets}
              onChange={(e) => set("source_assets", e.currentTarget.value)}
              hint="Slot, kind and licensing. Creators will only see files that have scanned clean." />
          </>
        ) : null}

        {step === "creators" ? (
          <>
            <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
              Anyone can join until the platform budgets are used. There is no participant cap and no invite list.
            </p>
            <TextInput label="Submissions per creator" required inputMode="numeric"
              value={form.submissions_per_creator}
              error={fieldError(form, "submissions_per_creator")}
              onChange={(e) => set("submissions_per_creator", e.currentTarget.value)}
              hint="How many posts one creator can send on each required platform." />
          </>
        ) : null}

        {step === "rewards" ? (
          <>
            <TextInput label={`Cap per creator (${currency})`} required inputMode="decimal"
              placeholder="0.00"
              value={form.cap}
              error={fieldError(form, "cap")}
              onChange={(e) => set("cap", e.currentTarget.value)}
              hint="The most one creator can earn on each platform. Tenths like 0.1 are fine." />
            <TextInput label={`Cap per post (${currency})`} inputMode="decimal" value={form.post_cap}
              error={fieldError(form, "post_cap")}
              onChange={(e) => set("post_cap", e.currentTarget.value)}
              hint="Optional. Cannot be higher than the per-creator cap." />
            {selectedPlatforms(form).length === 0 ? (
              <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)" }}>
                Choose the required platforms on the brief step. Each one gets its own budget and its own price per 1,000 views.
              </p>
            ) : selectedPlatforms(form).map((id) => {
              const label = DRAFT_PLATFORMS.find((p) => p.id === id)?.label ?? id;
              return (
                <div key={id} className="cm-draft-pair">
                  <TextInput label={`${label} budget (${currency})`} required inputMode="decimal"
                    placeholder="0.00"
                    value={form.platform_budgets[id] ?? ""}
                    error={fieldError(form, `budget:${id}`) ?? fieldError(form, "platform_budget")}
                    onChange={(e) => setPlatform("platform_budgets", id, e.currentTarget.value)}
                    hint="This platform's share of the pool." />
                  <TextInput label={`${label} per 1,000 views (${currency})`} required inputMode="decimal"
                    placeholder="0.00"
                    value={form.platform_cpms[id] ?? ""}
                    error={fieldError(form, `cpm:${id}`)}
                    onChange={(e) => setPlatform("platform_cpms", id, e.currentTarget.value)}
                    hint="What you pay for 1,000 counted views on this platform." />
                </div>
              );
            })}
            <LiveCampaignTotal form={form} currency={currency} />
          </>
        ) : null}

        {step === "schedule" ? (
          <>
            <div className="cm-draft-pair">
              <TextInput label="Opening date" type="date" value={form.join_opens_at}
                onChange={(e) => set("join_opens_at", e.currentTarget.value)}
                hint="The first day creators can join. Leave blank to open as soon as the campaign is live." />
              <TextInput label="Content submission date" type="date" required
                value={form.submission_deadline_at}
                error={fieldError(form, "submission_deadline_at")}
                onChange={(e) => set("submission_deadline_at", e.currentTarget.value)}
                hint="Last day a creator can submit a post." />
            </div>
            <TextInput label="Campaign end date" type="date" required
              value={form.campaign_ends_at}
              error={fieldError(form, "campaign_ends_at")}
              onChange={(e) => set("campaign_ends_at", e.currentTarget.value)}
              hint="When you start reviewing submitted videos and distributing earnings. Views stop counting on this date." />
            <div className="cm-draft-pair">
              <TextInput label="Tracking days" required inputMode="numeric" value={form.tracking_days}
                error={fieldError(form, "tracking_days")}
                onChange={(e) => set("tracking_days", e.currentTarget.value)}
                hint="How long we keep counting views after a post goes live. Counting also stops on the campaign end date." />
            </div>
            <div className="cm-draft-pair">
              <TextInput label="Minimum live days" required inputMode="numeric" value={form.min_live_days}
                error={fieldError(form, "min_live_days")}
                onChange={(e) => set("min_live_days", e.currentTarget.value)}
                hint="How many days the post must stay up." />
              <TextInput label="Earning hold days" required inputMode="numeric"
                value={form.earning_hold_days}
                error={fieldError(form, "earning_hold_days")}
                onChange={(e) => set("earning_hold_days", e.currentTarget.value)}
                hint="Days after a view counts before the earning can be withdrawn." />
            </div>
          </>
        ) : null}

        {step === "preview" ? (
          <Preview form={form} currency={currency} issues={issues} onJump={go}
            coverUrl={cover?.id ? campaignCoverUrl(campaignId, cover.id) : null} />
        ) : null}
      </section>

      <div style={{ display: "flex", gap: space(2), alignItems: "center", flexWrap: "wrap" }}>
        <Button type="button" variant="ghost" onClick={back} disabled={idx === 0}>Back</Button>
        {idx < WIZARD_STEPS.length - 1 ? (
          <Button type="button" variant="primary" onClick={next}>Continue</Button>
        ) : (
          <Button type="button" variant="primary" onClick={() => void send()}
                  disabled={submitBusy || saving || !ready}>
            {submitBusy ? "Sending…" : "Send for review"}
          </Button>
        )}
        <Button type="button" variant="secondary" onClick={() => void persist("save")} disabled={saving}>
          {saving ? "Saving…" : "Save draft"}
        </Button>
        {canDiscardDraft && onDiscardDraft ? (
          <Button type="button" variant="ghost" disabled={discardBusy || saving}
            onClick={onDiscardDraft}>
            {discardBusy ? "Cancelling…" : "Cancel edit"}
          </Button>
        ) : null}
        {step === "preview" && !ready ? (
          <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>
            {issues.length} {issues.length === 1 ? "item" : "items"} still needed.
          </span>
        ) : (
          <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>
            Sending for review never activates the campaign.
          </span>
        )}
      </div>
    </Card>
  );
}

function LiveCampaignTotal({ form, currency }: { form: DraftFormState; currency: string }) {
  const total = liveCampaignTotalMinor(form);
  if (total == null) return null;
  const asMoney = (v: number) => fromMinorString(String(v), currency as "GBP" | "USD" | "EUR");
  return (
    <div style={{
      display: "grid", gap: 6, padding: "12px 14px",
      border: "1px solid var(--line)", borderRadius: 10, background: "var(--surface-2)",
    }}>
      <Eyebrow>Campaign total</Eyebrow>
      <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--text)", fontWeight: 650 }}>
        <Money value={asMoney(total)} />
      </p>
      <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--faint)", lineHeight: 1.5 }}>
        Sum of the platform budgets. The minimum is $100.
      </p>
    </div>
  );
}

function Preview({ form, currency, issues, onJump, coverUrl }: {
  form: DraftFormState; currency: string;
  issues: ReturnType<typeof issuesFor>;
  onJump: (id: WizardStepId) => void;
  coverUrl?: string | null;
}) {
  return (
    <div style={{ display: "grid", gap: space(3) }}>
      {issues.length > 0 ? (
        <div className="cm-wizard-issues" role="status">
          <Eyebrow>Still needed</Eyebrow>
          <ul>
            {issues.map((i) => (
              <li key={`${i.step}-${i.field}`}>
                <button type="button" className="cm-wizard-issue" onClick={() => onJump(i.step)}>
                  {WIZARD_STEPS.find((s) => s.id === i.step)?.title}: {i.message}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
          Preview only. Creators will not see this until staff approve it and
          every activation gate passes. Review is not funding, and funding is
          not live.
        </p>
      )}

      <VersionPreview form={form} currency={currency} coverUrl={coverUrl} />
    </div>
  );
}
